import { Observation } from "@medplum/fhirtypes";
import { FhirBundleSdk } from "@metriport/fhir-sdk";
import { ISO_DATE } from "@metriport/shared/common/date";
import dayjs from "dayjs";
import {
  getColorForInterpretation,
  getLabReports,
  getReportName,
  getRenderValue,
  getValueAndInterpretation,
  renderLabReferenceRange,
} from "../../../shared-components/fhir-sections/labs/shared";
import {
  getResourceIdFromReference,
  isUnknown,
} from "../../../shared-components/fhir-sections/shared";
import {
  getObservationCode as getObservationCodeForGrouping,
  getObservationDate,
  groupResourcesByKey,
  ObservationSeries,
} from "../../table-transforms/series-utils";

type ChartSeries = {
  name: string;
  data: Array<{ id?: string; date: string; value: number }>;
  color?: string;
};

/** Get hover color for interpretation - darker shade of the same color */
function getHoverColorForInterpretation(interpretation: string | undefined): string | undefined {
  switch (interpretation) {
    case "low":
      return "rgba(40, 120, 175, 0.35)"; // Darker blue
    case "high":
      return "rgba(175, 70, 100, 0.35)"; // Darker red
    case "abnormal":
      return "rgba(175, 70, 100, 0.35)"; // Darker red
    case "normal":
      return "rgba(55, 175, 70, 0.35)"; // Darker green
    default:
      return undefined;
  }
}

/**
 * A lab series (all observations of the same type). Display fields show one observation
 * (most recent, or a specific one for panel drill-down). Full series data is preserved
 * for side panel charts. focusedObservationId indicates which observation to highlight.
 */
export type LabRowData = {
  id: string;
  observation: string;
  value: string;
  interpretation: string;
  referenceRange: string;
  date: string;
  originalData: ObservationSeries;
  backgroundColor?: string;
  hoverColor?: string;
  chartSeries: ChartSeries[];
  focusedObservationId: string;
};

/** Row data for panel view - represents a lab panel (DiagnosticReport) */
export type LabPanelRowData = {
  id: string;
  name: string;
  date: string;
  labCount: number;
};

/** Map of panel ID to its drill-down lab rows */
export type LabPanelDrillDownData = Map<string, LabRowData[]>;

/**
 * Treats placeholder display "unknown" like a missing display
 * so observation code text can be used instead.
 */
export function getLabObservationColumnName(observation: Observation): string {
  const display = observation.code?.coding?.[0]?.display?.trim();
  if (display && !isUnknown(display)) {
    return display;
  }
  if (observation.code?.text) {
    return observation.code.text;
  }
  return "-";
}

function extractObservationData(
  series: ObservationSeries
): Array<{ id?: string; date: string; value: number }> {
  const data: Array<{ id?: string; date: string; value: number }> = [];

  for (const resource of series.resources) {
    const date = getObservationDate(resource);
    if (!date || date === "-") continue;

    const value = resource.valueQuantity?.value;
    if (value === undefined) continue;

    data.push({ id: resource.id, date, value });
  }

  return data;
}

export function getLabRows({ bundle }: { bundle: FhirBundleSdk }): LabRowData[] {
  // Get only lab observations (category = "laboratory")
  const observations = bundle.getObservations().filter(observation => {
    return (
      observation.category?.some(cat =>
        cat.coding?.some(coding => coding.code?.toLowerCase() === "laboratory")
      ) ?? false
    );
  });

  // Group observations by code into series
  const observationSeries = groupResourcesByKey(
    observations,
    getObservationCodeForGrouping,
    getObservationDate,
    "Observation"
  );

  // Convert series to row data
  return observationSeries.map(series => {
    const latestObservation = series.resources[series.resources.length - 1]; // Last in array is newest

    if (!latestObservation) {
      throw new Error("Series should always have at least one resource");
    }

    const { value, unit, interpretation, referenceRange } =
      getValueAndInterpretation(latestObservation);

    const observationName = getLabObservationColumnName(latestObservation);

    return {
      id: `series-${series.code}`,
      observation: observationName,
      value: getRenderValue(value, unit),
      interpretation: interpretation || "-",
      referenceRange: renderLabReferenceRange(referenceRange, unit),
      date: formatDate(series.latestDate),
      originalData: series,
      backgroundColor: getColorForInterpretation(interpretation),
      hoverColor: getHoverColorForInterpretation(interpretation),
      chartSeries: [
        {
          name: observationName,
          data: extractObservationData(series),
        },
      ],
      focusedObservationId: latestObservation.id ?? "",
    };
  });
}

export function getLabPanelRows({ bundle }: { bundle: FhirBundleSdk }): {
  panelRows: LabPanelRowData[];
  drillDownData: LabPanelDrillDownData;
} {
  const allLabRows = getLabRows({ bundle });

  const diagnosticReports = bundle.getDiagnosticReports();
  const labReports = getLabReports(diagnosticReports);

  // Build a lookup: observation ID -> LabRowData
  // Each LabRowData's originalData.resources contains all observations in that series
  const observationToRowMap = new Map<string, LabRowData>();
  for (const row of allLabRows) {
    for (const obs of row.originalData.resources) {
      if (obs.id) {
        observationToRowMap.set(obs.id, row);
      }
    }
  }

  // Group rows by panel, creating drill-down rows with correct focused observation
  const drillDownData: LabPanelDrillDownData = new Map();

  // for each diagnostic report, create a drill-down row for each observation in the report
  const panelRows = labReports
    .flatMap(report => {
      const resultRefs = report.result ?? [];

      const drillDownRows: LabRowData[] = [];
      for (const ref of resultRefs) {
        const obsId = getResourceIdFromReference(ref.reference);
        if (!obsId) continue;

        // get the LabRowData that has this observation in its originalData.resources
        const baseRow = observationToRowMap.get(obsId);
        if (!baseRow) continue;

        // find the observation in the originalData.resources
        const focusedObs = baseRow.originalData.resources.find(r => r.id === obsId);
        if (!focusedObs) continue;

        // prevent duplicate drill-down rows
        // diagnostic report reference the same observation multiple times
        const drillDownId = `${baseRow.id}-${obsId}`;
        if (drillDownRows.some(r => r.id === drillDownId)) continue;

        const { value, unit, interpretation, referenceRange } =
          getValueAndInterpretation(focusedObs);

        const drillDownRow: LabRowData = {
          ...baseRow,
          id: drillDownId,
          value: getRenderValue(value, unit),
          date: formatDate(getObservationDate(focusedObs)),
          interpretation: interpretation || "-",
          referenceRange: renderLabReferenceRange(referenceRange, unit),
          backgroundColor: getColorForInterpretation(interpretation),
          hoverColor: getHoverColorForInterpretation(interpretation),
          focusedObservationId: obsId,
        };

        drillDownRows.push(drillDownRow);
      }

      if (!drillDownRows.length) return [];

      const panelId = report.id ?? "-";
      drillDownData.set(panelId, drillDownRows);

      return {
        id: panelId,
        name: getReportName(report) ?? "Unknown panel",
        date: formatDate(report.effectiveDateTime ?? report.effectivePeriod?.start),
        labCount: drillDownRows.length,
      };
    })
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  return { panelRows, drillDownData };
}

function formatDate(date: string | undefined): string {
  if (!date || date === "-") return "-";
  try {
    const parsed = dayjs(date);
    if (!parsed.isValid()) return "-";
    return parsed.format(ISO_DATE);
  } catch {
    return "-";
  }
}
